import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;
    dialog?.removeEventListener('cancel', this.#handleCancel);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Ensures dialog-modal elements use the top layer (showModal), not a non-modal open state.
   * Non-modal opens are trapped by #header-component { contain: layout style } and mis-position the cart drawer.
   * @param {HTMLDialogElement} dialog
   */
  #ensureDialogModalLayer(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.classList.contains('dialog-modal')) return;
    if (!dialog.open || dialog.matches(':modal')) return;

    dialog.close();
    dialog.showModal();
  }

  /** @param {HTMLDialogElement} dialog */
  #bindDialogInteraction(dialog) {
    dialog.addEventListener('cancel', this.#handleCancel);
    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open && dialog.matches(':modal')) return;

    if (dialog.open && dialog.classList.contains('dialog-modal')) {
      this.#ensureDialogModalLayer(dialog);
      this.#bindDialogInteraction(dialog);
      this.dispatchEvent(new DialogOpenEvent());
      return;
    }

    if (dialog.open) return;

    const isBodyFixed = document.body.style.position === 'fixed';
    const bodyTop = Number.parseInt(document.body.style.top || '0', 10);
    const scrollY = isBodyFixed && Number.isFinite(bodyTop) ? Math.abs(bodyTop) : window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // Guard against stale callbacks when component state changes before frame runs.
      if (!this.isConnected || !dialog.isConnected) return;

      if (dialog.open) {
        if (dialog.matches(':modal')) return;
        if (dialog.classList.contains('dialog-modal')) {
          this.#ensureDialogModalLayer(dialog);
          this.#bindDialogInteraction(dialog);
          this.dispatchEvent(new DialogOpenEvent());
        }
        return;
      }

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.#bindDialogInteraction(dialog);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    dialog.removeEventListener('cancel', this.#handleCancel);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const hasOtherOpenDialogs = document.querySelectorAll('dialog[open]').length > 1;

    // If another modal dialog is still open (e.g. quick-add -> cart handoff),
    // keep body lock and scroll position management delegated to that dialog.
    if (!hasOtherOpenDialogs) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Handles the cancel event when user clicks the backdrop or presses Escape.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    // Prevent the browser's default behavior of closing the dialog
    event.preventDefault();
    // Instead, use our close animation
    this.closeDialog();
  };

  /**
   * Closes the dialog when the user presses the escape key.
   * When multiple dialogs are open the topmost one handles Escape via its
   * native `cancel` event — skip here to avoid closing a background dialog.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    // Let the topmost modal dialog handle Escape via its `cancel` event.
    if (document.querySelectorAll('dialog[open]').length > 1) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Closes the dialog when a click is detected outside the dialog.
   * This serves as a fallback for backdrop clicks when the dialog's
   * native cancel event doesn't fire (e.g., when body is position: fixed).
   * Clicks inside another stacked dialog (e.g. quick-add over cart drawer)
   * are ignored so only the topmost dialog reacts.
   *
   * @param {PointerEvent} event - The click event.
   */
  #handleDocumentClick = (event) => {
    // Don't close when the click landed inside another open dialog.
    if (document.querySelectorAll('dialog[open]').length > 1) {
      const isInsideAnotherDialog = [...document.querySelectorAll('dialog[open]')].some(
        (d) => d !== this.refs.dialog && d.contains(/** @type {Node} */ (event.target))
      );
      if (isInsideAnotherDialog) return;
    }

    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
